// EditorJS 공통 핵심 모듈
// Notes와 Page에서 공유하는 EditorJS 초기화, 도구 설정, 블록 마이그레이션
//
// 사용법:
//   import { createEditorInstance, migrateBlocks } from '@/editors/editor_core'
//
import EditorJS from '@editorjs/editorjs';
import Header from '@/editorjs/tools/header/index';
import Quote from "@/editorjs/tools/quote/index";
import Delimiter from '@/editorjs/tools/delimiter/index';
import EditorjsList from '@editorjs/list';
import Table from '@editorjs/table';
import Marker from '@editorjs/marker';
import Underline from '@editorjs/underline';
import DragDrop from 'editorjs-drag-drop';
import RawTool from '@editorjs/raw';
import Paragraph from '@editorjs/paragraph';
import Embed from "@/editorjs/tools/embed/index";
import GalleryTool from "@/utils/gallery_tool";
import TextColor from "editorjs-color";
import Button from "@/editorjs/tools/button/index";
import Strikethrough from "editorjs-strikethrough";
import InlineCode from "@/editors/inline-code/index";
import ShikiCodeTool from '@/editorjs/tools/shiki-code/index';
import Columns from '@/editorjs/tools/columns';
import SimpleImageTool from "@/editorjs/tools/simple-image/index";
import MarkdownTool from "@/editorjs/tools/markdown/index";
import UnifiedUndo from "@/editorjs/tools/unified-undo/index";
import LinkTool from '@/editorjs/tools/link/index';
import VoteTool from '@/editorjs/tools/vote/index';
import AiPolishTool from '@/editorjs/tools/ai-polish/index';
import FootnoteTool from '@/editorjs/tools/footnote/index';
import { setupFootnotePanel } from '@/editorjs/tools/footnote/panel';
import { setupParagraphTransformer } from '@/editorjs/paragraph_transformer';

import BlockStyleTune from '@/editorjs/tunes/block-style/tune';
import TextStyleTune from '@/editorjs/tunes/text-style/tune';
import DuplicateTune from '@/editorjs/tunes/duplicate/tune';

import { createImageUploaderConfig } from '@/utils/image_uploader_config';
import korean from "@/i18n/editorjs-korean";

// 특수 블록
import SubscribeFormTool from '@/editorjs/tools/subscribe-form/index';
import MembershipCtaTool from '@/editorjs/tools/membership-cta/index';
import RecentPostsTool from '@/editorjs/tools/recent-posts/index';
import PostEmbedTool from '@/editorjs/tools/post-embed/index';
import SpecialBlockPickerTool from '@/editorjs/tools/special-block-picker/index';


/**
 * EditorJS 블록 데이터 마이그레이션 (레거시 포맷 → 현재 포맷)
 */
export function migrateBlocks(data) {
  if (!data || !data.blocks) return data;

  data.blocks = data.blocks.map((block) => {
    switch (block.type) {
      case "list":
        return legacyListToNestedList(block);
      case "code":
        return migrateCodeBlockToColumn(block);
      default:
        return block;
    }
  });

  return data;
}

function legacyListToNestedList(block) {
  if (block.data && block.data.items && block.data.items.length > 0) {
    if (typeof block.data.items[0] === "string") {
      block.data.items = block.data.items.map((item) => ({
        content: item,
        items: []
      }));
    }
  }
  return block;
}

function migrateCodeBlockToColumn(block) {
  if (block.type !== "code" || !block.data?.code) return block;
  return {
    type: "columns",
    data: {
      cols: [
        {
          blocks: [
            {
              type: "paragraph",
              data: { text: block.data.code }
            }
          ]
        }
      ],
      style: "bordered"
    }
  };
}

/**
 * EditorJS 공통 도구 설정 생성
 *
 * @param {Object} options
 * @param {Object} options.controller - Stimulus 컨트롤러 인스턴스 (이미지 업로드 등에 필요)
 * @param {boolean} options.lightMode - 간소화 모드 (paragraph만)
 * @param {boolean} options.includeAdvanced - 고급 도구 포함 (vote, markdown, raw 등)
 * @param {string} options.spaceUrl - Writer space URL
 * @param {string} options.postExtId - Post/Page ext_id
 * @param {string} options.currentPlan - 현재 요금제
 * @param {string} options.primaryTextColor
 * @param {string} options.primaryBackgroundColor
 */
export function buildTools(options = {}) {
  const {
    controller,
    lightMode = false,
    includeAdvanced = false,
    excludeVote = false,
    excludeAiPolish = false,
    includePageWidgets = false,
    spaceUrl = '',
    postExtId = '',
    currentPlan = 'basic',
    primaryTextColor = '#000000',
    primaryBackgroundColor = '#FFFFFF'
  } = options;

  if (lightMode) {
    return {
      tools: {
        paragraph: {
          class: Paragraph,
          tunes: [],
          inlineToolbar: true,
          config: { preserveBlank: true }
        }
      },
      defaultBlockTunes: []
    };
  }

  // 데모 에디터(space_url="demo")에서는 요금제 제한 해제 + 서버 저장 없이 툴 시연만.
  const isDemo = spaceUrl === "demo";

  const imageToolConfig = createImageUploaderConfig(controller);
  const defaultBlockTunes = ['duplicate'];

  const getCommonTools = (extraOptions = {}) => {
    const { includeColumns = false, columnsTool = null } = extraOptions;

    const tools = {
      blockStyle: BlockStyleTune,
      textStyle: TextStyleTune,
      duplicate: DuplicateTune,
      paragraph: {
        class: Paragraph,
        tunes: ['textStyle', ...defaultBlockTunes],
        inlineToolbar: true,
        config: { preserveBlank: true }
      },
      header: {
        class: Header,
        tunes: ['textStyle', ...defaultBlockTunes],
        inlineToolbar: true,
        shortcut: 'CMD+SHIFT+H',
        config: {
          levels: [1, 2, 3, 4, 5, 6],
          defaultLevel: 2,
          placeholder: '헤더 내용을 입력하세요',
        },
      },
    };

    Object.assign(tools, {
      linkTool: {
        class: LinkTool,
        shortcut: 'CMD+L',
        config: { endpoint: '/api/links/parse' }
      },
      image: {
        class: GalleryTool,
        shortcut: 'CMD+SHIFT+I',
        config: imageToolConfig
      },
      list: {
        class: EditorjsList,
        shortcut: 'CMD+SHIFT+L',
        inlineToolbar: true,
      },
      quote: {
        class: Quote,
        inlineToolbar: true,
        shortcut: 'CMD+SHIFT+O',
        config: {
          quotePlaceholder: '내용을 입력하세요',
          captionPlaceholder: '\t'
        },
      },
      delimiter: {
        class: Delimiter,
        shortcut: 'CMD+SHIFT+D',
      },
      ...(includeColumns && columnsTool ? { columns: columnsTool } : {}),
      table: {
        class: Table,
        shortcut: 'CMD+SHIFT+E',
        inlineToolbar: true,
        config: { rows: 2, cols: 2 },
      },
      AnyButton: {
        class: Button,
        inlineToolbar: false,
        shortcut: 'CMD+SHIFT+A',
        config: {
          css: { "btnColor": "editor-button" },
          defaultAlignment: "center",
          colorSettingLink: spaceUrl !== "demo" && `/${spaceUrl}/o/settings/home#primary_colors`,
          defaultTextColor: primaryTextColor,
          defaultBackgroundColor: primaryBackgroundColor,
        }
      },
      strikethrough: {
        class: Strikethrough,
        shortcut: 'CMD+SHIFT+X',
      },
      inlineCode: {
        class: InlineCode,
        shortcut: 'CMD+E',
      },
      underline: {
        class: Underline,
        inlineToolbar: true,
        shortcut: "CMD+U"
      },
      footnote: { class: FootnoteTool },
      ColorPicker: TextColor,
      Marker: {
        class: Marker,
        shortcut: 'CMD+M',
      },
      embed: {
        class: Embed,
        config: {
          services: { youtube: true, telegram: true },
          // URL 붙여넣기 시 자동 embed 변환 끔 — url_insert_menu에서 사용자가 직접 선택
          disablePastePatterns: true
        }
      },
    });

    return tools;
  };

  // 고급 도구
  const advancedTools = !includeAdvanced ? {} : {
    ...(postExtId !== "form" && !excludeAiPolish && {
      aiPolish: { class: AiPolishTool },
    }),
    ...(postExtId !== "form" && !excludeVote && {
      vote: {
        class: VoteTool,
        inlineToolbar: false,
        config: {
          endpoint: `/api/${spaceUrl}/notes/${postExtId}/votes`,
          // 데모에서는 요금제 제한 없이 툴을 써볼 수 있게 잠금 해제.
          canUseVote: isDemo || ["branding", "marketing"].includes(currentPlan),
          // 데모에서는 서버에 Vote를 저장(vote_id 발급)하지 않고 툴 동작만 시연.
          isDemo,
          restrictionMessage: "투표 기능은 브랜딩 플랜부터 사용 가능합니다.",
          restrictionButtonText: "요금제 변경하기",
          restrictionButtonUrl: `/${spaceUrl}/o/settings/plan`,
        },
        ...(!(isDemo || ["branding", "marketing"].includes(currentPlan)) && {
          toolbox: { title: "🔒 투표(브랜딩플랜)" }
        })
      },
    }),
    ShikiCodeTool: { class: ShikiCodeTool },
    raw: {
      class: RawTool,
      config: {
        placeholder: "이메일에서 작동하는 HTML 코드만 넣으셔야 합니다."
      }
    },
  };

  // Columns 설정
  const columnInternalTools = {
    ...getCommonTools(),
    image: {
      class: SimpleImageTool,
      shortcut: 'CMD+SHIFT+I',
      config: imageToolConfig
    },
    // Page 모드일 때만 위젯 도구 추가 (Note에서는 Column 내부에 위젯 삽입 불가)
    ...(includePageWidgets ? {
      special_block_picker: {
        class: SpecialBlockPickerTool,
        config: { includePageWidgets: true },
      },
      subscribe_form: {
        class: SubscribeFormTool,
        config: {
          defaultTextColor: primaryTextColor,
          defaultBackgroundColor: primaryBackgroundColor,
          spaceUrl,
        },
        toolbox: false,
      },
      membership_cta: {
        class: MembershipCtaTool,
        config: {
          defaultTextColor: primaryTextColor,
          defaultBackgroundColor: primaryBackgroundColor,
          spaceUrl,
        },
        toolbox: false,
      },
      post_embed: {
        class: PostEmbedTool,
        config: { spaceUrl, pageMode: true },
        toolbox: false,
      },
      recent_posts: {
        class: RecentPostsTool,
        config: { spaceUrl },
        toolbox: false,
      },
    } : {}),
  };

  const markdownTools = {
    ...getCommonTools(),
    ShikiCodeTool: { class: ShikiCodeTool },
  };

  const nestedEditorConfig = {
    EditorJsLibrary: EditorJS,
    tools: columnInternalTools,
    tunes: defaultBlockTunes,
    registerUndoManager: (blockId, colIndex, editorInstance) => {
      if (controller?.undo) controller.undo.registerNestedEditor(blockId, colIndex, editorInstance);
      if (controller?._collabNestedEditors) {
        controller._collabNestedEditors.set(`${blockId}-${colIndex}`, editorInstance);
      }
    },
    unregisterUndoManager: (blockId) => {
      if (controller?.undo) controller.undo.unregisterNestedEditors(blockId);
      if (controller?._collabNestedEditors) {
        for (const key of controller._collabNestedEditors.keys()) {
          if (key.startsWith(`${blockId}-`)) controller._collabNestedEditors.delete(key);
        }
      }
    },
    updateMainEditorState: () => {
      if (controller?.undo) controller.undo.registerChange();
    },
    setupParagraphTransformer,
  };

  const columnsTool = {
    class: Columns,
    tunes: ['blockStyle', ...defaultBlockTunes],
    config: nestedEditorConfig
  };

  if (includeAdvanced) {
    advancedTools.markdown = {
      class: MarkdownTool,
      shortcut: "CMD+SHIFT+M",
      config: { tools: markdownTools },
    };
  }

  // Page 위젯 (Page 편집에서만 활성화)
  // 특수 블록: picker + 개별 블록 (toolbox 숨김, picker에서 삽입)
  const specialBlocks = !includeAdvanced ? {} : {
    special_block_picker: {
      class: SpecialBlockPickerTool,
      config: { includePageWidgets },
    },
    // 개별 블록은 toolbox에서 숨기되 등록은 유지 (picker에서 insert 시 필요)
    subscribe_form: {
      class: SubscribeFormTool,
      config: {
        defaultTextColor: primaryTextColor,
        defaultBackgroundColor: primaryBackgroundColor,
        spaceUrl,
      },
      toolbox: false,
    },
    membership_cta: {
      class: MembershipCtaTool,
      config: {
        defaultTextColor: primaryTextColor,
        defaultBackgroundColor: primaryBackgroundColor,
        spaceUrl,
      },
      toolbox: false,
    },
    post_embed: {
      class: PostEmbedTool,
      config: { spaceUrl, pageMode: includePageWidgets },
      toolbox: false,
    },
    ...(includePageWidgets ? {
      recent_posts: {
        class: RecentPostsTool,
        config: { spaceUrl },
        toolbox: false,
      },
    } : {}),
  };

  return {
    tools: {
      ...getCommonTools({ includeColumns: true, columnsTool }),
      ...specialBlocks,
      ...advancedTools,
    },
    defaultBlockTunes,
  };
}

/**
 * EditorJS 인스턴스 생성
 *
 * @param {Object} options
 * @param {string} options.holder - 에디터 컨테이너 DOM ID
 * @param {Object} options.data - 초기 블록 데이터
 * @param {Object} options.tools - buildTools()에서 반환된 도구 설정
 * @param {string[]} options.tunes - 기본 tunes
 * @param {boolean} options.lightMode - 간소화 모드
 * @param {Function} options.onReady - 에디터 준비 완료 콜백
 */
export function createEditorInstance({ holder, data, tools, tunes, lightMode = false, collabMode = false, onReady }) {
  const migratedData = migrateBlocks(data);

  const editor = new EditorJS({
    onReady: () => {
      if (!editor) return;

      const holderElement = document.getElementById(holder);
      if (!holderElement || !document.body.contains(holderElement)) return;

      // Undo — collab 모드에서는 Y.UndoManager를 사용하므로 UnifiedUndo 스킵
      let undo = null;
      if (!collabMode) {
        undo = new UnifiedUndo({ editor });
        undo.initialize(migratedData);
      }

      // DragDrop
      try { new DragDrop(editor); } catch (e) { /* DOM이 사라진 경우 무시 */ }

      // Paragraph Transformer
      const paragraphCleanup = setupParagraphTransformer(editor, holderElement);

      // 각주 패널 (lightMode가 아닌 경우만)
      let footnoteCleanup = null;
      if (!lightMode) {
        footnoteCleanup = setupFootnotePanel(holderElement);
      }

      if (onReady) {
        onReady({ editor, undo, holderElement, paragraphCleanup, footnoteCleanup });
      }
    },
    logLevel: process.env.NODE_ENV === 'production' ? 'ERROR' : 'ERROR',
    data: migratedData,
    holder,
    placeholder: "'/'를 눌러 블록을 선택하거나, 원하는 내용을 입력하세요.",
    i18n: korean,
    tools,
    tunes: tunes || ['duplicate'],
  });

  return editor;
}

export {
  EditorJS, UnifiedUndo, DragDrop, setupParagraphTransformer, setupFootnotePanel, korean,
  createImageUploaderConfig
};
